import { inject, InjectionToken, Service } from '@angular/core';
import { environment } from '@env/environment';

import { PlatformService } from '@core/platform/platform.service';
import { LogCategory, LogContext, LogEvent, LogLevel } from './log-event';
import { LOG_SINK } from './log-sink';

/** Minimum level config (overridable via DI for tests / per-env tuning). */
export const LOGGER_MIN_LEVEL = new InjectionToken<LogLevel>('LOGGER_MIN_LEVEL', {
  providedIn: 'root',
  factory: () => (environment.production ? LogLevel.Warn : LogLevel.Trace),
});

/**
 * A logger bound to a fixed `source` — what components/services actually use.
 * Created via `LoggerService.forSource('Name')`.
 */
export interface ScopedLogger {
  trace(message: string, context?: LogContext): void;
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, context?: LogContext): void;
  critical(message: string, context?: LogContext): void;
  /** Structured event with an explicit semantic category. */
  event(category: LogCategory, level: LogLevel, message: string, context?: LogContext): void;
  // semantic tracking (carry the bound source)
  lifecycle(message: string, context?: LogContext): void;
  route(message: string, context?: LogContext): void;
  theme(message: string, context?: LogContext): void;
  language(message: string, context?: LogContext): void;
  translation(message: string, context?: LogContext): void;
  home(message: string, context?: LogContext): void;
  uiEvent(message: string, context?: LogContext): void;
  userAction(message: string, context?: LogContext): void;
}

/**
 * LoggerService — the single logging entry point for /v2.
 *
 * - SSR-safe: console is used ONLY in development in the browser; production
 *   and the server route events to the LOG_SINK (default no-op) → "no console
 *   output in production".
 * - Environment-aware: min level derives from `environment.production`.
 * - Tree-shakeable & typed: no `any`, structured LogEvent payloads.
 * - Signal-friendly: synchronous, side-effect-only (safe to call from effects).
 * - Dev ergonomics: pretty, colour-grouped output with stack traces on errors.
 *
 * Use `forSource('ThemeFacade')` to get a ScopedLogger, or call the category
 * helpers (`lifecycle`, `route`, `theme`, `language`, …) directly.
 */
@Service()
export class LoggerService {
  private readonly platform = inject(PlatformService);
  private readonly sink = inject(LOG_SINK);
  private readonly minLevel = inject(LOGGER_MIN_LEVEL);

  private readonly devConsole = !environment.production && this.platform.isBrowser;
  private readonly correlationId = this.createCorrelationId();

  // ── generic level API ────────────────────────────────────────────────
  trace(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Trace, message, context, source);
  }
  debug(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Debug, message, context, source);
  }
  info(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Info, message, context, source);
  }
  warn(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Warn, message, context, source);
  }
  error(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Error, message, context, source);
  }
  protected critical(message: string, context?: LogContext, source?: string): void {
    this.emit('general', LogLevel.Critical, message, context, source);
  }

  // ── semantic tracking helpers (all Info-level by default) ─────────────
  lifecycle(message: string, context?: LogContext): void {
    this.emit('lifecycle', LogLevel.Info, message, context);
  }
  route(message: string, context?: LogContext): void {
    this.emit('route', LogLevel.Info, message, context);
  }
  theme(message: string, context?: LogContext): void {
    this.emit('theme', LogLevel.Info, message, context);
  }
  language(message: string, context?: LogContext): void {
    this.emit('language', LogLevel.Info, message, context);
  }
  translation(message: string, context?: LogContext): void {
    this.emit('translation', LogLevel.Info, message, context);
  }
  home(message: string, context?: LogContext): void {
    this.emit('home', LogLevel.Info, message, context);
  }
  uiEvent(message: string, context?: LogContext): void {
    this.emit('ui-event', LogLevel.Debug, message, context);
  }
  protected userAction(message: string, context?: LogContext): void {
    this.emit('user-action', LogLevel.Info, message, context);
  }

  /** Bind a fixed source name for cleaner call sites. */
  forSource(source: string): ScopedLogger {
    return {
      trace: (m, c) => this.emit('general', LogLevel.Trace, m, c, source),
      debug: (m, c) => this.emit('general', LogLevel.Debug, m, c, source),
      info: (m, c) => this.emit('general', LogLevel.Info, m, c, source),
      warn: (m, c) => this.emit('general', LogLevel.Warn, m, c, source),
      error: (m, c) => this.emit('general', LogLevel.Error, m, c, source),
      critical: (m, c) => this.emit('general', LogLevel.Critical, m, c, source),
      event: (cat, lvl, m, c) => this.emit(cat, lvl, m, c, source),
      lifecycle: (m, c) => this.emit('lifecycle', LogLevel.Info, m, c, source),
      route: (m, c) => this.emit('route', LogLevel.Info, m, c, source),
      theme: (m, c) => this.emit('theme', LogLevel.Info, m, c, source),
      language: (m, c) => this.emit('language', LogLevel.Info, m, c, source),
      translation: (m, c) => this.emit('translation', LogLevel.Info, m, c, source),
      home: (m, c) => this.emit('home', LogLevel.Info, m, c, source),
      uiEvent: (m, c) => this.emit('ui-event', LogLevel.Debug, m, c, source),
      userAction: (m, c) => this.emit('user-action', LogLevel.Info, m, c, source),
    };
  }

  // ── core ───────────────────────────────────────────────────────────────
  private emit(
    category: LogCategory,
    level: LogLevel,
    message: string,
    context?: LogContext,
    source?: string,
  ): void {
    if (level < this.minLevel) return;

    const event: LogEvent = {
      level,
      category,
      message,
      source,
      context,
      timestamp: new Date().toISOString(),
      correlationId: this.correlationId,
      stack: level >= LogLevel.Error && this.devConsole ? new Error().stack : undefined,
    };

    // Always offer the event to the sink (remote/no-op).
    try {
      this.sink.write(event);
    } catch {
      /* a failing sink must never break the app */
    }

    // Pretty console ONLY in dev+browser.
    if (this.devConsole) this.prettyPrint(event);
  }

  private prettyPrint(event: LogEvent): void {
    const tag = `%c${LogLevel[event.level]}%c ${event.category}${event.source ? ' · ' + event.source : ''}`;
    const badge = `background:${this.colorFor(event.level)};color:#fff;padding:1px 6px;border-radius:4px;font-weight:700`;
    const meta = 'color:#888';
    // eslint-disable-next-line no-console
    const c = console;
    const hasCtx = !!event.context && Object.keys(event.context).length > 0;

    if (hasCtx || event.stack) {
      c.groupCollapsed(tag, badge, meta, event.message);
      if (hasCtx) c.log(event.context);
      if (event.stack) c.log('%c' + event.stack, 'color:#b00');
      c.log('%c' + event.timestamp + ' · ' + event.correlationId, meta);
      c.groupEnd();
    } else {
      c.log(tag, badge, meta, event.message);
    }
  }

  private colorFor(level: LogLevel): string {
    if (level >= LogLevel.Critical) return '#7c1d1d';
    if (level >= LogLevel.Error) return '#c0392b';
    if (level >= LogLevel.Warn) return '#c89a45';
    if (level >= LogLevel.Info) return '#1b38b0';
    return '#5e6873';
  }

  private createCorrelationId(): string {
    const c = this.platform.window?.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return 'tb-' + Math.random().toString(36).slice(2, 10);
  }
}
